import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  PageContainer,
  Header,
  BackButton,
  PageTitle,
  ContentContainer,
  PhotoCarousel,
  CarouselContainer,
  CarouselImage,
  CarouselPlaceholder,
  CarouselIndicators,
  CarouselDot,
  PreviewCard,
  ItemName,
  ItemPrice,
  ItemDescription,
  CategoryTag,
  OptionGroupsSection,
  OptionGroupsTitle,
  OptionGroupsSummary,
  OptionGroup,
  OptionGroupName,
  OptionGroupOptions,
  CreateButton,
  LoadingSpinner,
  SuccessMessage,
  SuccessIcon,
  SuccessTitle,
  SuccessText,
  Overlay,
} from "../../styles/ReviewFoodMenu.styled";
import { POST } from "../../services/HTTPServices";
import { ROUTES } from "../../constants/routes";

interface FoodItem {
  id?: string;
  item: string;
  name: string;
  photoPath: string;
  price: number;
  description: string;
  fullDesc: string;
  categories: any[];
  isAvailable: boolean;
  estPreparationTime: string;
  isEighteenPlus: boolean;
  foodOptionsTypeList: any[];
  images?: string[];
}

const ReviewFoodMenu: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [foodItem, setFoodItem] = useState<FoodItem | null>(null);

  useEffect(() => {
    // Get food item data from navigation state or localStorage
    const itemData = location.state?.foodItem || 
                    JSON.parse(localStorage.getItem("reviewFoodItem") || "null");
    
    if (itemData) {
      setFoodItem(itemData);
    } else {
      // If no data, redirect back to add menu
      navigate("/food/entry/-1");
    }
  }, [location.state, navigate]);

  const handleBack = () => {
    navigate(-1);
  };

  const getImages = () => {
    if (!foodItem) return [];
    
    // Check for multiple image sources
    const images = [];
    
    if (foodItem.images && foodItem.images.length > 0) {
      images.push(...foodItem.images);
    }
    
    if (foodItem.photoPath && !images.includes(foodItem.photoPath)) {
      images.push(foodItem.photoPath);
    }
    
    return images.filter(img => img && img.trim() !== "");
  };

  const getCategoryName = () => {
    if (!foodItem?.categories || foodItem.categories.length === 0) {
      return "Uncategorized";
    }
    return foodItem.categories[0]?.name || "Main Course";
  };

  const getOptionGroupsSummary = () => {
    if (!foodItem?.foodOptionsTypeList || foodItem.foodOptionsTypeList.length === 0) {
      return null;
    }

    return foodItem.foodOptionsTypeList.map(group => {
      const options = group.foodOptionsList?.map((option: any) => option.name).join("/") || "";
      return `${group.name}: ${options}`;
    }).join(" • ");
  };

  const handleCreateItem = async () => {
    if (!foodItem) return;

    setIsCreating(true);

    try {
      // Prepare the data for API call
      const createData = {
        ...foodItem,
        serviceProviderId: JSON.parse(localStorage.getItem("authUser") || "{}")?.id || "",
      };

      // API call to create the food item
      const response = await POST("fooddelivery/CreateMenu", createData, "provider");

      if (response && response.status === 200) {
        // Show success message
        setShowSuccess(true);
        
        // Clear the review data from localStorage
        localStorage.removeItem("reviewFoodItem");
        
        // Navigate back to food menu after 2 seconds
        setTimeout(() => {
          setShowSuccess(false);
          navigate(ROUTES.FOOD_PAGE);
        }, 2000);
      } else {
        throw new Error("Failed to create menu item");
      }
    } catch (error) {
      console.error("Error creating menu item:", error);
      alert("Failed to create menu item. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  const images = getImages();
  const hasImages = images.length > 0;

  if (!foodItem) {
    return (
      <PageContainer>
        <div>
          <LoadingSpinner />
          Loading...
        </div>
      </PageContainer>
    );
  }

  return (
    <>
      <PageContainer>
        <Header>
          <BackButton onClick={handleBack}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path 
                d="M15 18L9 12L15 6" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </BackButton>
          <PageTitle>Review Item</PageTitle>
        </Header>

        <ContentContainer>
          {/* Photo Carousel */}
          <PhotoCarousel>
            <CarouselContainer>
              {hasImages ? (
                <CarouselImage 
                  src={images[currentImageIndex]} 
                  alt={foodItem.item}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              ) : (
                <CarouselPlaceholder>
                  No images uploaded
                </CarouselPlaceholder>
              )}
            </CarouselContainer>
            
            {hasImages && images.length > 1 && (
              <CarouselIndicators>
                {images.map((_, index) => (
                  <CarouselDot
                    key={index}
                    active={index === currentImageIndex}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                ))}
              </CarouselIndicators>
            )}
          </PhotoCarousel>

          {/* Item Details Preview */}
          <PreviewCard>
            <ItemName>{foodItem.item || foodItem.name}</ItemName>
            <ItemPrice>${foodItem.price?.toFixed(2) || "0.00"}</ItemPrice>
            
            {foodItem.description && (
              <ItemDescription>{foodItem.description}</ItemDescription>
            )}
            
            <CategoryTag>{getCategoryName()}</CategoryTag>
            
            {getOptionGroupsSummary() && (
              <OptionGroupsSection>
                <OptionGroupsTitle>Available Options</OptionGroupsTitle>
                <OptionGroupsSummary>
                  {foodItem.foodOptionsTypeList.map((group, index) => (
                    <OptionGroup key={index}>
                      <OptionGroupName>{group.name}:</OptionGroupName>{" "}
                      <OptionGroupOptions>
                        {group.foodOptionsList?.map((option: any) => option.name).join(", ") || "None"}
                      </OptionGroupOptions>
                    </OptionGroup>
                  ))}
                </OptionGroupsSummary>
              </OptionGroupsSection>
            )}
          </PreviewCard>
        </ContentContainer>

        <CreateButton 
          onClick={handleCreateItem}
          disabled={isCreating}
        >
          {isCreating && <LoadingSpinner />}
          {isCreating ? "Creating Item..." : "Create Item"}
        </CreateButton>
      </PageContainer>

      {/* Success Modal */}
      {showSuccess && (
        <>
          <Overlay />
          <SuccessMessage>
            <SuccessIcon>
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path 
                  d="M20 6L9 17L4 12" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </SuccessIcon>
            <SuccessTitle>Awesome!</SuccessTitle>
            <SuccessText>
              Your new item has been added to the menu.
            </SuccessText>
          </SuccessMessage>
        </>
      )}
    </>
  );
};

export default ReviewFoodMenu;
